<template>
  <entity-edit-dialog
    @submit="submit"
    @close="close"
    v-model="visible"
    ref="createDeviceDialog"
    :dialog-title="dialogTitle"
    :submitting="submitting"
  >
    <template v-slot:contentCard>
      <v-card-text>
        <div class="my-5" v-if="error && !submitting">
          <p class="alertText">{{ $t('common.errors.error') }}</p>
          <p>{{ error }}</p>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.location') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <location-select v-model="deviceFormData.location" />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold required">{{
                $t('device.identification')
              }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="deviceFormData.identification"
                ref="identificationInput"
                outlined
                dense
                hide-details="auto"
                :rules="[(v) => !!v || $t('validation.required')]"
                required
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold required">{{ $t('device.name') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="deviceFormData.name"
                outlined
                dense
                hide-details="auto"
                :rules="[(v) => !!v || $t('validation.required')]"
                required
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.manufacturer') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="deviceFormData.manufacturer"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{
                $t('device.manufacturingNumber')
              }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="deviceFormData.manufacturingNumber"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{
                $t('device.manufacturingYear')
              }}</span>
            </v-col>
            <v-col cols="12" sm="2">
              <v-text-field
                v-model="deviceFormData.manufacturingYear"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.purchasePrice') }}</span>
            </v-col>
            <v-col cols="11" sm="4">
              <v-text-field
                v-model="deviceFormData.purchasePrice"
                type="number"
                class="right-align-number-input"
                min="0"
                step=".01"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
            <v-col cols="1" sm="1">
              <span class="span-bold">€</span>
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.importer') }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <SupplierSelectField
                :show-select-dialog-button="true"
                :show-add-dialog-button="true"
                :dense="true"
                v-model="deviceFormData.importer"
                :label="$t('actions.select')"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.note') }}</span>
            </v-col>
            <v-col cols="12" sm="8">
              <v-textarea
                v-model="deviceFormData.note"
                rows="3"
                outlined
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.externalUrl') }}</span>
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="deviceFormData.externalUrl"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.criticalParts') }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <PartQuantitiesSelect v-model="deviceFormData.criticalParts" />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.generalParts') }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <PartQuantitiesSelect v-model="deviceFormData.generalParts" />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.suppliers') }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <MultipleSuppliersSelect v-model="suppliers" />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('device.serviceManagers') }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <MultipleUsersSelectWithEmailNotification
                v-model="deviceFormData.serviceManagers"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{
                $t('device.defectNotificationEmails')
              }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <MultipleEmailsAdd
                v-model="additionalEmails"
                @removeEmail="removeEmail"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{
                $t('plannedService.documents')
              }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <documents-select v-model="documents" />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" offset-sm="3" sm="8">
              <v-checkbox
                dense
                v-model="disabled"
                :label="$t('device.disabled')"
              ></v-checkbox>
              <span class="span-bold">{{ $t('device.disabledText') }}</span>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </template>
  </entity-edit-dialog>
</template>

<script>
import EntityEditDialog from '@/components/entity-crud/EntityEditDialog';
import i18n from '@/i18n';
import DevicesService from '@/services/DevicesService';
import DocumentsSelect from '@/components/inputs/DocumentsSelect';
import SupplierSelectField from '@/components/inputs/SupplierSelectField';
import PartQuantitiesSelect from '@/components/inputs/PartQuantitiesSelect';
import MultipleSuppliersSelect from '@/components/inputs/MultipleSuppliersSelect';
import MultipleUsersSelectWithEmailNotification from '@/components/inputs/MultipleUsersSelectWithEmailNotification';
import MultipleEmailsAdd from '@/components/inputs/MultipleEmailsAdd';
import LocationSelect from '@/components/inputs/LocationSelect';

// WARNING: Do not use this as an example on how to do anything

function createData() {
  return {
    active: true,
    location: null,
    identification: null,
    name: null,
    manufacturer: '',
    manufacturingNumber: '',
    manufacturingYear: '',
    purchasePrice: null,
    importer: null,
    note: '',
    externalUrl: '',
    criticalParts: [],
    generalParts: [],
    serviceManagers: [],
    defectNotificationEmails: [],
  };
}

export default {
  name: 'DeviceFormDialog',

  components: {
    EntityEditDialog,
    DocumentsSelect,
    SupplierSelectField,
    PartQuantitiesSelect,
    MultipleSuppliersSelect,
    MultipleEmailsAdd,
    MultipleUsersSelectWithEmailNotification,
    LocationSelect,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    deviceData: {
      type: Object,
    },
  },

  data: () => {
    return {
      deviceFormData: createData(),
      suppliers: [],
      documents: [],
      additionalEmails: [],
      disabled: false,
      submitting: false,
      error: null,
    };
  },

  computed: {
    decimalStep() {
      if (!this.deviceFormData.decimals || this.deviceFormData.decimals < 1) {
        return undefined;
      }

      let nulls = '';
      for (let index = 1; index < this.deviceFormData.decimals; index++) {
        nulls += '0';
      }
      const step = `.${nulls}1`;

      return step;
    },

    priceUnit() {
      return `€ / ${this.unitName}`;
    },

    mapToItem(device) {
      return {
        text: this.$options.filters.device(device),
        value: device,
      };
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    dialogTitle() {
      return i18n.t('device.singular');
    },
  },

  methods: {
    removeEmail(id) {
      this.additionalEmails = this.additionalEmails.filter(
        (email) => email.id !== id,
      );
    },

    close() {
      this.deviceFormData = createData();

      this.documents = [];
      this.suppliers = [];
      this.additionalEmails = [];
      this.disabled = false;
      this.submitting = false;
      this.error = null;

      this.$emit('dialogClosed');
    },

    submit() {
      if (this.submitting) {
        return;
      }
      this.error = null;

      if (!this.$refs.createDeviceDialog.$refs.form.validate()) {
        this.$refs.createDeviceDialog.showFormHasValidationErrorsMessage(
          i18n.t('plannedService.requiredInformationMissing'),
        );
        return;
      }

      const deviceDTO = {
        device: this.deviceFormData,
        suppliers: this.suppliers,
        documents: this.documents,
      };
      deviceDTO.device.active = !this.disabled;
      deviceDTO.device.defectNotificationEmails = this.additionalEmails.map(
        (email) => email.input,
      );

      this.submitting = true;
      DevicesService.addDevice(deviceDTO)
        .then((result) => {
          this.$emit('selectedDevice', result.data);
          this.close();
        })
        .catch((err) => {
          this.error =
            err.response.data.message ||
            err.message ||
            err.toString() ||
            'Some Network Error';

          this.$refs.identificationInput.focus();
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.required:after {
  content: ' *';
  color: red;
}

.span-bold {
  font-weight: bolder;
}

.alertText {
  color: red;
  font-weight: bolder;
}
</style>
